
/**
 * Search widget for menus.
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.4.0
 * @copyright  Copyright (C) 2021-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2021-12-30 (1.0.0) (jhoward)
 *    - Refactored from textual-menu.vue to be used with visual-menu.vue as well.
 *
 *  2022-01-11 (1.1.0) (jhoward)
 *    - Support fixed positioning.
 *
 *  2022-03-07 (1.1.1) (jhoward)
 *    - Do not show menu search results if search input is blank.
 *
 *  2022-05-11 (1.2.0) (jhoward)
 *    - Update minimized layout.
 *
 *  2022-05-24 (1.2.1) (jhoward)
 *    - <search-menu> now uses .visual_menu--glass_effect for stylization.
 *    - <search-menu> refactored handle click logic to handleSearchHandleClick() method.
 *
 *  2023-04-21 (1.3.0) (jhoward)
 *    - <search-menu> now uses TypeScript.
 *
 *  2023-08-03 (1.3.1) (jhoward)
 *    - Erase search term after submitting search or closing the search widget in <search-menu>.
 *
 *  2023-04-21 (1.3.2) (jhoward)
 *    - Add `open` and `close` event listeners on <search-menu> container element.
 *    - Coding style improvements in <search-menu>.
 *
 *  2023-08-26 (1.3.3) (jhoward)
 *    - Use store for selected items in <search-menu>.
 *
 *  2023-12-03 (1.3.4) (jhoward)
 *    - Layout improvements in <search-menu>.
 *    - Add isPublicDevice prop to <search-menu>.
 *    - Only clear search results and term if isPublicDevice prop is set to true in <search-menu>.
 *    - Add isCartEnabled prop to <search-menu>.
 *    - Do not show "favorite" icon in search results if isCartEnabled prop is set to false in <search-menu>.
 *
 *  2025-04-08 (1.4.0) (jhoward)
 *    - Refactor layout in <search-menu> to show more information in a way that is easier to read.
 *    - Remove favorite button in <search-menu>.
 *    - Add "view-menu-item" emits in <search-menu>.
 *    - Add "relatedMenus" prop in <search-menu>.
 *    - Search all menus available on the page in <search-menu>.
 *    - Add <search-menu>::allMenuIdsByTitle computed property.
 *    - Add <search-menu>::allMenuIconsByTitle computed property.
 *    - Add <search-menu>::getResultTitle() method.
 *    - Add <search-menu>::getResultData() method.
 *    - Add <search-menu>::getMenuItemTitle() method.
 *    - Add <search-menu>::isItemScheduledInAnyMenu() method.
 *    - Add <search-menu>::isItemScheduledInMenu() method.
 *    - Add <search-menu>::isItemScheduledInSection() method.
 *    - Track user interaction in <search-menu>.
 */

import {defineComponent, PropType} from "vue";
import TextInput from '../material-form/text-input.vue';
import {Menu, MenuSection, SearchMenuComponent, SearchMenuEmitViewMenuItemPayload} from "./defs";
import {TextInputComponent} from "../material-form/defs";
import {methodIsModelScheduled} from "../../helpers/menu-sections";

interface AutocompleteResult {
  [key: string]: any;

  menu: string;
  menu_id: number;
  mi_highlight: number;
  mi_item_group_name: string;
  mi_item_subgroup_name: string;
  mi_price: string;
  mi_id: number;
  mi_title: string;
  mi_subtitle: string;
  mi_description: string;
  ms_id: number;
  mt_title: string;
  section: string;
  tags: string[];
  _formatted: {
    [key: string]: any;

    menu: string;
    menu_id: number;
    mi_id: number;
    mi_item_group_name: string;
    mi_item_subgroup_name: string;
    mi_highlight: number;
    mi_price: string;
    mi_title: string;
    mi_subtitle: string;
    mi_description: string;
    ms_id: number;
    mt_title: string;
    section: string;
    tags: string[];
  };
}

interface AutocompleteResponse {
  exhaustiveNbHits: boolean;
  hits?: AutocompleteResult[];
  limit: number;
  nbHits: number;
  offset: number;
  processingTimeMs: number;
  query: string;
}

export default defineComponent({
  name: "search-menu",
  emits: {
    'view-menu-item': (payload: SearchMenuEmitViewMenuItemPayload) => true
  },
  components: {
    TextInput,
  },
  props: {
    searchIndex: {
      type: String,
      default: ''
    },
    searchKey: {
      type: String,
      default: ''
    },
    searchHost: {
      type: String,
      default: ''
    },
    menu: {
      type: Object as PropType<Menu | null>,
      default: null
    },
    relatedMenus: {
      type: Array as PropType<Menu[]>,
      default: () => []
    },
    minimizeWidget: {
      type: Boolean,
      default: false
    },
    isPublicDevice: {
      type: Boolean,
      default: false
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      searchTerm: '',
      autocompleteResults: [] as AutocompleteResult[],
      showResults: false,
      hideWidget: this.minimizeWidget,
      observer: null as IntersectionObserver | null,
      isStickyStuck: false,
      isFixedPosition: false,
      windowScrollY: 0,
      windowHeight: window.innerHeight,
      autocompleteMaxHeightRender: 0,

      abortController: null as AbortController | null,
      abortSignal: null as AbortSignal | null,
    };
  },
  mounted() {
    this.isFixedPosition = (window.getComputedStyle(this.$el).position === 'fixed');

    this.setupIntersectionObserver();

    document.addEventListener('click', () => {
      this.showResults = false;
      if (this.minimizeWidget) {
        this.hideWidget = true;
      }
    });

    window.addEventListener('scroll', () => {
      this.windowScrollY = window.scrollY;
    }, {passive: true});

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
  },
  watch: {
    autocompleteResults() {
      this.autocompleteMaxHeightRender++;
    },
    hideWidget() {
      if (this.hideWidget) {
        this.reset();
        window.pepTracking.event(this.$store.getters.displayTypeCapitalized + ' Menu', 'Close Search');
      } else {
        window.pepTracking.event(this.$store.getters.displayTypeCapitalized + ' Menu', 'Open Search');
      }
    }
  },
  computed: {
    autocompleteMaxHeight() {
      this.windowScrollY;
      this.autocompleteMaxHeightRender;
      let maxHeight = this.windowHeight;

      if (this.$refs.autocomplete) {
        maxHeight -= (this.$refs.autocomplete as HTMLLIElement).getBoundingClientRect().top;
      }

      if (this.$parent?.$el) {
        const sectionNavNode = this.$parent.$el.querySelector('.menu--section_nav');
        if (sectionNavNode) {
          maxHeight -= sectionNavNode.clientHeight;
        }
      }

      return maxHeight + 'px';
    },

    containerClasses() {
      return {
        'search--container-minimize': this.minimizeWidget,
        'search--container-open': this.minimizeWidget && !this.hideWidget,
        'sticky-stuck': this.isStickyStuck || this.isFixedPosition
      };
    },

    allMenuIdsByTitle(): { [key: string]: number } {
      const output: { [key: string]: number } = {};
      if (this.menu) {
        output[this.menu.menu_type.mt_title] = this.menu.menu_id;
      }
      this.relatedMenus.forEach(relatedMenu => {
        output[relatedMenu.menu_type.mt_title] = relatedMenu.menu_id;
      });
      return output;
    },

    allMenuIconsByTitle(): { [key: string]: string } {
      const output: { [key: string]: string } = {};
      if (this.menu) {
        output[this.menu.menu_type.mt_title] = this.menu.menu_icon;
      }
      this.relatedMenus.forEach(relatedMenu => {
        output[relatedMenu.menu_type.mt_title] = relatedMenu.menu_icon;
      });
      return output;
    },

  },
  methods: {
    isModelScheduled: methodIsModelScheduled,

    getResultTitle(result: AutocompleteResult): string {
      let output = this.getResultData(result, 'mi_title');
      const sectionTitle = this.getResultData(result, 'section');
      if (sectionTitle.length) {
        output += ' <span class="autocomplete_results--menu_title_section">in ' + sectionTitle + '</span>';
      }
      return output;
    },

    getResultData<K extends keyof AutocompleteResult>(result: AutocompleteResult, key: K): string {
      if (result._formatted[key].length) {
        if ('tags' === key) {
          return result._formatted.tags.join(', ');
        }
        return result._formatted[key];
      } else if (result[key].length) {
        if ('tags' === key) {
          return result.tags.join(', ');
        }
        return result[key];
      }
      return '';
    },
    getMenuItemTitle(result: AutocompleteResult): string {
      const titleParts = [];
      const menuTitle = this.getResultData(result, 'menu');
      if (menuTitle.length) {
        titleParts.push(menuTitle);
      }
      if (this.allMenuIconsByTitle[result.menu]) {
        titleParts.push('<i class="material-icons">' + this.allMenuIconsByTitle[result.menu] + '</i>');
      }
      const menuItemTitle = this.getResultData(result, 'mi_title');
      if (menuItemTitle.length) {
        titleParts.push(menuItemTitle);
      }
      return '<span>' + titleParts.join('</span><span>') + '</span>';
    },

    reset(force: boolean = false) {
      if (this.isPublicDevice || force) {
        if (this.$refs?.input) {
          (this.$refs.input as TextInputComponent).currentValue = '';
        }
        this.autocompleteResults = [];
      }
      this.showResults = false;
    },

    checkStickyStuck(entries: IntersectionObserverEntry[]) {
      this.isStickyStuck = (entries[0].intersectionRatio < 1);
    },

    setupIntersectionObserver() {
      if (window?.IntersectionObserver && !this.isFixedPosition) {
        //only need to set up the observer once
        if (!this.observer) {
          this.observer = new IntersectionObserver(this.checkStickyStuck, {
            threshold: [1]
          });
          if (this.$el.previousElementSibling) {
            this.observer.observe(this.$el.previousElementSibling);
          } else {
            this.observer.observe(this.$el);
          }
        }
      }
    },

    focusHandler() {
      if (this.searchTerm !== '') {
        this.showResults = true;
      }
    },

    submitAutocomplete() {
      if (this.searchTerm === '') {
        this.showResults = false;
      } else {

        if (this.abortSignal && this.abortController) {
          this.abortController.abort();
        }

        if (window?.AbortController) {
          this.abortController = new AbortController();
          this.abortSignal = this.abortController.signal;
        }

        fetch(this.searchHost + '/indexes/' + this.searchIndex + '/search', {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
            'X-Meili-API-Key': this.searchKey
          },
          redirect: 'follow',
          body: JSON.stringify({
            q: this.searchTerm,
            filter: 'menu_id=' + Object.values(this.allMenuIdsByTitle).join(' OR menu_id='),
            cropLength: 50,
            attributesToCrop: ['mi_title', 'mi_subtitle', 'mi_description'],
            attributesToHighlight: ['mi_title', 'mi_subtitle', 'mi_description', 'section'],
          }),
          signal: this.abortSignal
        })
          .then(response => {
            return response.json();
          })
          .then((json: AutocompleteResponse) => {
            if (json?.hits) {
              this.autocompleteResults = json.hits.filter((result: AutocompleteResult) => this.isItemScheduledInAnyMenu(result.mi_id));
              this.showResults = true;
            } else {
              this.autocompleteResults = [];
              this.showResults = false;
            }
            window.pepTracking.event(this.$store.getters.displayTypeCapitalized+ ' Menu', 'Search Results', this.searchTerm);
          })
          .catch(e => {
            console.log(e);
          })
          .finally(() => {
            this.abortController = null;
            this.abortSignal = null;
          })
      }
    },

    submitSearch() {
      if (!this.searchTerm.length) {
        this.reset();
      } else {
        //TODO: filter down to selected results?
      }
    },

    handleSearchHandleClick() {
      this.hideWidget = !this.hideWidget;

      if (!this.hideWidget) {
        (this.$refs.input as TextInputComponent | null)?.$el.querySelector('input')?.focus();
      } else {
        this.showResults = false;
      }
    },

    handleResultClick(result: AutocompleteResult) {
      this.$emit('view-menu-item', {menu_id: result.menu_id, mi_id: result.mi_id});
      this.showResults = false;
      this.hideWidget = true;
      this.reset();
      window.pepTracking.event(this.$store.getters.displayTypeCapitalized + ' Menu', 'Search View Item', result.mi_title + ' (' + result.mi_id + ')');
    },

    isItemScheduledInAnyMenu(mi_id: number): boolean {
      if (this.menu && this.isItemScheduledInMenu(mi_id, this.menu)) {
        return true;
      }

      for (let i = 0, l = this.relatedMenus.length; i < l; i++) {
        if (this.isItemScheduledInMenu(mi_id, this.relatedMenus[i])) {
          return true;
        }
      }

      return false;
    },

    isItemScheduledInMenu(mi_id: number, menu: Menu): boolean {
      for (let i = 0, l = menu.sections.length; i < l; i++) {
        if (this.isItemScheduledInSection(mi_id, menu.sections[i])) {
          return true;
        }
      }

      return false;
    },

    isItemScheduledInSection(mi_id: number, section: MenuSection): boolean {
      for (let groupTitle in section.grouped_items) {
        for (let subgroupTitle in section.grouped_items[groupTitle]) {
          for (let i = 0, l = section.grouped_items[groupTitle][subgroupTitle].length; i < l; i++) {
            if (section.grouped_items[groupTitle][subgroupTitle][i].mi_id === mi_id) {
              return (this as SearchMenuComponent).isModelScheduled(section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules);
            }
          }
        }
      }

      for (let i = 0, l = section.children.length; i < l; i++) {
        if (this.isItemScheduledInSection(mi_id, section.children[i])) {
          return true;
        }
      }

      return false;
    },

  }
})
